import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMutation } from '@tanstack/react-query';
import { useSdk } from '../sdk';
import { useEffect, useState } from 'react';

// Centralized flag configuration
interface FlagConfig {
    id: string;
    launchDarklyKey: string;
    label: string;
    contextKind?: 'organization' | 'user';
}

interface FeatureFlag extends FlagConfig {
    contextKind: 'organization' | 'user';
    checked: boolean;
    isLoading?: boolean;
}

const FLAG_CONFIG: FlagConfig[] = [
    {
        id: 'actions-v2',
        launchDarklyKey: 'actionsV2',
        label: 'Actions v2',
        contextKind: 'organization',
    },
    {
        id: 'show-agent-in-sidebar',
        launchDarklyKey: 'showAgentInSidebar',
        label: 'Sidebar: Your agent',
    },
    {
        id: 'shadcn-search-enabled',
        launchDarklyKey: 'shadcnSearchEnabled',
        label: 'Enable classic prospect search',
    },
    {
        id: 'show-add-contacts',
        launchDarklyKey: 'showAddContacts',
        label: 'Sidebar: Add contacts',
    },
    {
        id: 'show-playbooks',
        launchDarklyKey: 'showPlaybooks',
        label: 'Sidebar: Playbooks',
    },
    {
        id: 'show-discovery',
        launchDarklyKey: 'showDiscovery',
        label: 'Sidebar: Discovery',
    },
    {
        id: 'show-global-admin',
        launchDarklyKey: 'showGlobalAdmin',
        label: 'Sidebar: Global admin',
    },
    {
        id: 'feed-v-2-chat',
        launchDarklyKey: 'feedV2Chat',
        label: 'Display chat in Feed v2 (old, disable me)',
    },
    {
        id: 'show-contact-type-in-contacts-table',
        launchDarklyKey: 'showContactTypeInContactsTable',
        label: 'Show contact type in contacts table',
    },
];

export const useFeatureFlags = () => {
    'use no memo';
    const allFlags = useFlags();
    const {
        user: { updateBetaFeatures: updateBetaFeaturesMutation },
    } = useSdk();
    const { mutate: updateBetaFeatures } = useMutation(updateBetaFeaturesMutation());

    const [flagStates, setFlagStates] = useState<Record<string, boolean>>({});
    const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

    useEffect(() => {
        // Update flag states from LaunchDarkly flags
        const newFlagStates = FLAG_CONFIG.reduce((obj, config) => {
            obj[config.id] = allFlags[config.launchDarklyKey] || false;
            return obj;
        }, {} as Record<string, boolean>);

        setFlagStates(newFlagStates);
        // Clear loading states when flags update (indicating the toggle completed)
        setLoadingStates({});
    }, [allFlags]);

    const toggle = (item: FeatureFlag) => {
        // Set loading state to true when starting the toggle
        setLoadingStates((prev) => ({ ...prev, [item.id]: true }));

        if (item.id) {
            updateBetaFeatures({ toggleFlag: { key: item.id, contextKind: item.contextKind } });
        }
    };

    // Generate feature flags from centralized config
    const featureFlags: FeatureFlag[] = FLAG_CONFIG.map((config) => ({
        id: config.id,
        label: config.label,
        launchDarklyKey: config.launchDarklyKey,
        contextKind: config.contextKind || 'user',
        checked: flagStates[config.id],
        isLoading: loadingStates[config.id] || false,
    }));

    return {
        featureFlags,
        toggle,
    };
};
